import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inboxApi } from '@wa/shared';

/**
 * Where a selection goes: one of the tenant's fixed lists, or a new one named
 * here. Used by the members panel (copy / move) and the broadcast report
 * ("make a list of everyone who failed"), so both land people the same way.
 *
 * Resolves with `{ targetSegmentId }` or `{ newListName }`, or null.
 */
export type ListTarget = { targetSegmentId: number } | { newListName: string };

export function ListTargetDialog({ title, subtitle, excludeId, busy, error, onPick, onClose }: {
  title: string;
  subtitle?: string;
  /** The list the selection is already in — not offered as a destination. */
  excludeId?: number | null;
  busy?: boolean;
  error?: string | null;
  onPick: (target: ListTarget) => void;
  onClose: () => void;
}) {
  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments });
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<number | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lists = (segments.data ?? [])
    .filter((s) => s.kind === 0 && s.id !== excludeId)
    .filter((s) => !q.trim() || s.name.toLowerCase().includes(q.trim().toLowerCase()));
  const canGo = mode === 'new' ? name.trim().length > 0 : picked !== null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <form
        role="dialog" aria-modal="true" aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!canGo || busy) return;
          onPick(mode === 'new' ? { newListName: name.trim() } : { targetSegmentId: picked! });
        }}
        className="flex max-h-[76vh] w-full max-w-sm flex-col rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)"
      >
        <header className="border-b border-(--color-line) px-4 pt-3.5 pb-3">
          <h2 className="text-lead font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="mt-0.5 text-micro leading-snug text-(--color-muted)">{subtitle}</p>}
        </header>

        <div className="flex gap-1 px-3 pt-3">
          {(['existing', 'new'] as const).map((m) => (
            <button
              key={m} type="button" onClick={() => setMode(m)}
              className={`tap rounded-full px-3 py-1 text-small font-medium ${mode === m ? 'bg-(--color-brand-wash) text-(--color-brand)' : 'text-(--color-muted) hover:bg-(--color-sunken)'}`}
            >
              {m === 'existing' ? 'An existing list' : 'A new list'}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {mode === 'new' ? (
            <label className="block">
              <span className="text-micro font-medium text-(--color-muted)">List name</span>
              <input
                autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
                placeholder="HRMS — failed, 5 Oct"
                className="mt-1 h-10 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 text-body outline-none focus:border-(--color-brand)"
              />
            </label>
          ) : (
            <>
              {lists.length + (q ? 1 : 0) > 6 || q ? (
                <input
                  value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lists" aria-label="Search lists"
                  className="mb-2 h-9 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 text-small outline-none focus:border-(--color-brand)"
                />
              ) : null}
              {segments.isPending && <p className="py-4 text-center text-small text-(--color-muted)">Loading…</p>}
              {segments.data && lists.length === 0 && (
                <p className="py-4 text-center text-small text-(--color-muted)">No other fixed list. Make a new one.</p>
              )}
              <ul className="flex flex-col">
                {lists.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button" onClick={() => setPicked(s.id)} aria-pressed={picked === s.id}
                      className={`tap flex w-full items-center gap-2 rounded-(--radius-field) px-2.5 py-2 text-left ${picked === s.id ? 'bg-(--color-brand-wash)' : 'hover:bg-(--color-sunken)'}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-body">{s.name}</span>
                      <span className="shrink-0 text-micro text-(--color-muted) tabular-nums">{(s.memberCount ?? 0).toLocaleString('en-IN')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {error && <p role="alert" className="mt-2 rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">{error}</p>}
        </div>

        <footer className="flex justify-end gap-2 border-t border-(--color-line) px-3 py-2.5">
          <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
          <button
            type="submit" disabled={!canGo || busy}
            className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-60"
          >
            {busy ? 'Working…' : mode === 'new' ? 'Create and add' : 'Add'}
          </button>
        </footer>
      </form>
    </div>
  );
}

/** Hands a CSV blob to the browser as a download. */
export function saveCsv(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
